import React, { useRef, useEffect } from 'react'
import {
  Typography,
  makeStyles,
  IconButton,
  List,
  useTheme
} from '@material-ui/core'
import { useSpring, animated } from 'react-spring'
import {
  SidePanel,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter
} from '../../../../shared'
import clsx from 'clsx'
import {
  CloseIcon,
  SidebarCloseIcon,
  SidebarOpenIcon
} from '../../../icons'
import { useActiveView, useUiStore } from '../../app/hooks'
import SedaPreviewChartPanel from './SedaPreviewChartPanel'
import useCondensedPanel from '../hooks/useCondensedPanel'
import usePanelChartVisible from '../hooks/usePanelChartVisible'
import {
  SedaLocationsPanelButton,
  SedaFilterPanelButton,
  SedaMetricPanelButton,
  SedaRegionPanelButton,
  SedaSubgroupPanelButton
} from './buttons'
import useHoveredPanel from '../hooks/useHoveredPanel'
import useActivePanel from '../hooks/useActivePanel'
import useIsMobile from '../../app/hooks/useIsMobile'
import { readLocalStorage } from '../../../../shared/utils'

const useStyles = makeStyles(theme => ({
  root: {},
  title: {
    ...theme.mixins.boldType,
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    overflow: 'hidden'
  },
  details: { padding: 0 },
  body: {
    padding: '4px 0',
    overflowX: 'hidden',
    overflowY: 'auto'
  },
  list: {
    minWidth: theme.app.panelWidth
  },
  headerPanel: {
    minHeight: theme.spacing(7),
    minWidth: theme.spacing(9.5)
  },
  toggleCondensed: {
    position: 'absolute',
    right: 0,
    top: '100%',
    background: '#fff',
    transform: 'translate(50%, -50%)',
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '100%',
    zIndex: 999,
    transition: 'opacity 0.4s ease-in-out',
    '&.MuiIconButton-root': {
      marginRight: 0
    },
    '&:hover': {
      background: theme.palette.primary.main,
      color: '#fff'
    },
    '&:focus': {
      opacity: '1!important'
    }
  },
  footerPanel: {
    width: theme.app.panelWidth,
    height: 264,
    minWidth: theme.app.panelWidth,
    transformOrigin: 'left bottom'
  },
  footerCondensed: {
    position: 'absolute',
    left: theme.app.condensedPanelWidth + theme.spacing(3),
    bottom: 0,
    borderTop: 'none'
  },
  footerShowChart: {},
  footerNoChart: {
    position: 'absolute',
    bottom: 0
  }
}))

const AnimatedSidePanel = animated(SidePanel)
const AnimatedSidePanelFooter = animated(SidePanelFooter)

/**
 * Gets the offset to transform the panel footer containing the preview chart based on the current UI context
 * @param {*} param0
 */
const getFooterStyleProps = ({
  condensed,
  view,
  hovered,
  showChart,
  activePanel
}) => {
  // get vertical offset for preview chart
  const verticalOffset =
    !condensed && view === 'map'
      ? 0
      : view !== 'map' || hovered || activePanel
      ? 264
      : !showChart
      ? 216
      : showChart
      ? -24
      : 0
  return {
    transform: `translate(0px, ${verticalOffset}px)`,
    height: !condensed && !showChart ? 48 : 264
  }
}

/**
 * Expanded version of the control panel
 */
const SedaCollapsePanel = ({
  style: initialStyle,
  ...props
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useIsMobile()
  const [view] = useActiveView()
  const [activePanel, setActivePanel] = useActivePanel()
  const [panelCondensed, togglePanelCondensed] = useCondensedPanel()
  const [panelHovered, setPanelHovered] = useHoveredPanel()
  const [showChart] = usePanelChartVisible()
  const setShowPanelHint = useUiStore(store => store.setShowPanelHint)
  const panelStyle = useSpring({
    width:
      panelCondensed && !panelHovered && !activePanel
        ? theme.app.condensedPanelWidth
        : theme.app.panelWidth,
    delay: panelCondensed
      ? 200
      : 0
  })

  const footerStyleProps = getFooterStyleProps({
    view,
    condensed: panelCondensed,
    hovered: panelHovered,
    showChart,
    activePanel
  })
  const footerStyle = useSpring(footerStyleProps)
  const footerRef = useRef(null)

  // trigger panel expand on mouse enter
  const handleMouseEnter = e => {
    // ignore if it's the preview chart
    if(footerRef.current && footerRef.current.contains(e.target)) return
    if(e.target.closest('[role="tooltip"]')) return
    setPanelHovered(true)
  }
  // trigger panel collapse on mouse leave
  const handleMouseLeave = (e) => {
    if(e.target.closest('[role="tooltip"]')) return
    setPanelHovered(false)
  }

  const handleClose = () => {
    setActivePanel(null)
  }

  // toggle between condensed / expanded mode
  const handleToggleCondensed = () => {
    !panelCondensed && setPanelHovered(false)
    togglePanelCondensed()
  }

  useEffect(() => {
		if(readLocalStorage('disable-hint-metric', 'boolean') !== true) {
			setShowPanelHint('metric', true)
		}
	}, [])

  return (
    <AnimatedSidePanel
      style={{ ...initialStyle, ...panelStyle }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}>
      <SidePanelHeader className={classes.headerPanel} sticky>
        <Typography className={classes.title}>
          Data Options
        </Typography>
        {!isMobile ? (
          <IconButton
            className={classes.toggleCondensed}
            style={{
              opacity: !panelCondensed && !panelHovered ? 0 : 1
            }}
            onClick={handleToggleCondensed}>
            {panelCondensed ? (
              <SidebarOpenIcon />
            ) : (
              <SidebarCloseIcon />
            )}
          </IconButton>
        ) : (
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        )}
      </SidePanelHeader>
      <SidePanelBody classes={{ root: classes.body }}>
        <List className={classes.list}>
          <SedaMetricPanelButton />
          <SedaRegionPanelButton />
          <SedaSubgroupPanelButton />
          <SedaFilterPanelButton />
          <SedaLocationsPanelButton />
        </List>
      </SidePanelBody>
      {!isMobile && (
        <AnimatedSidePanelFooter
          sticky
          ref={footerRef}
          className={clsx(classes.footerPanel, {
            [classes.footerCondensed]: panelCondensed,
            [classes.footerShowChart]: showChart,
            [classes.footerNoChart]: view !== 'map'
          })}
          style={footerStyle}>
          <SedaPreviewChartPanel />
        </AnimatedSidePanelFooter>
      )}
    </AnimatedSidePanel>
  )
}

export default SedaCollapsePanel
